// @ts-check
/**
 * Único ponto de contato do front-end com o servidor.
 *
 * Nenhum componente chama `fetch` diretamente e nenhum arquivo do cliente
 * conhece o banco de dados. No app antigo, cada hook falava direto com o
 * Supabase usando a chave pública embutida no bundle.
 */

const BASE_URL = "/api";

/** Erro vindo da API, já com a mensagem que o usuário deve ler. */
export class ApiError extends Error {
  constructor(status, payload) {
    super(payload?.error?.message ?? "Não foi possível concluir a operação.");
    this.name = "ApiError";
    this.status = status;
    this.code = payload?.error?.code ?? "UNKNOWN";
    this.details = payload?.error?.details;
  }
}

/** Avisa o app quando a sessão morre, para redirecionar ao login uma vez só. */
const ouvintesDeSessao = new Set();

/** @param {() => void} callback */
export function onSessionExpired(callback) {
  ouvintesDeSessao.add(callback);
  return () => ouvintesDeSessao.delete(callback);
}

/**
 * Note que `body` aqui NÃO é o `BodyInit` do fetch: recebemos um valor
 * qualquer e serializamos para JSON abaixo. Por isso o tipo é montado à mão
 * em vez de estender `RequestInit`.
 *
 * @param {string} path
 * @param {Omit<RequestInit, "body"> & { params?: Record<string, unknown>, body?: unknown }} [options]
 */
async function request(path, options = {}) {
  const { params, headers, body, ...resto } = options;

  const url = new URL(BASE_URL + path, window.location.origin);
  if (params) {
    for (const [chave, valor] of Object.entries(params)) {
      if (valor !== undefined && valor !== null && valor !== "") {
        url.searchParams.set(chave, String(valor));
      }
    }
  }

  const resposta = await fetch(url, {
    // Manda o cookie httpOnly de sessão em toda requisição.
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...resto,
  });

  if (resposta.status === 204) return null;

  const payload = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    const erro = new ApiError(resposta.status, payload);

    // 401 em qualquer chamada significa que a sessão acabou. O login em si
    // é exceção: ali o 401 é "senha errada", e quem trata é a tela.
    if (resposta.status === 401 && !path.startsWith("/auth/login")) {
      ouvintesDeSessao.forEach((cb) => cb());
    }

    throw erro;
  }

  return payload;
}

export const api = {
  /** @param {string} path @param {Record<string, unknown>} [params] */
  get: (path, params) => request(path, { method: "GET", params }),

  /** @param {string} path @param {unknown} [body] */
  post: (path, body) => request(path, { method: "POST", body }),

  /** @param {string} path @param {unknown} [body] */
  patch: (path, body) => request(path, { method: "PATCH", body }),

  /** @param {string} path @param {unknown} [body] */
  put: (path, body) => request(path, { method: "PUT", body }),

  /** @param {string} path */
  delete: (path) => request(path, { method: "DELETE" }),
};
